import React, { useEffect, useState, useCallback } from "react";
import { Play, RefreshCw, FileText, Download, CheckCircle2, Send, Ban, AlertTriangle, Lock, Unlock, Banknote, Receipt, Pencil } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, CartesianGrid } from "recharts";
import api, { apiError } from "../lib/api";
import { PageHeader, Card, Badge, Loading, Modal, Spinner, fmtMoney, EmptyState } from "../components/ui";

const STAGES = ["Input", "Process", "Review", "Approve", "Release"];
const STATUS = {
  YET_TO_START: ["Yet to start", "neutral", 0],
  INPUT_SYNCED: ["Input synced", "info", 1],
  PROCESSING: ["Processing", "warn", 2],
  PROCESSED: ["Processed", "info", 2],
  DISCREPANCY: ["Discrepancy", "bad", 3],
  APPROVED: ["Approved", "warn", 4],
  RELEASED: ["Released", "ok", 5],
  CANCELLED: ["Cancelled", "bad", 0],
};

function downloadBlob(path, filename, setBusy) {
  setBusy && setBusy(true);
  api.get(path, { responseType: "blob" })
    .then((res) => {
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    })
    .catch((e) => alert(apiError(e.response?.data?.detail) || "Download failed"))
    .finally(() => setBusy && setBusy(false));
}

function Stepper({ stage }) {
  return (
    <div className="flex items-center gap-1" data-testid="payroll-stepper">
      {STAGES.map((s, i) => {
        const done = stage > i; const active = stage === i + 1;
        return (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
              done ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
              : active ? "bg-accent/15 text-accent" : "bg-slate-100 text-slate-400 dark:bg-slate-800"}`}>
              <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[9px] ${done ? "bg-emerald-500 text-white" : active ? "bg-accent text-white" : "bg-slate-300 text-white dark:bg-slate-600"}`}>{i + 1}</span>
              {s}
            </div>
            {i < STAGES.length - 1 && <span className="h-px w-3 bg-slate-200 dark:bg-slate-700" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function Payroll() {
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("processing");
  const [filters, setFilters] = useState({ company: "", payGroup: "", fy: "", empType: "all" });
  const [dash, setDash] = useState(null);
  const [structs, setStructs] = useState(null);
  const [runs, setRuns] = useState([]);
  const [slips, setSlips] = useState(null);   // { run, items }
  const [discRow, setDiscRow] = useState(null); // { row, items }
  const [editStruct, setEditStruct] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/payroll/meta").then(({ data }) => {
      setMeta(data);
      const firstCo = data.companies.find((c) => c.active);
      const firstPg = data.payGroups.find((p) => p.companyId === firstCo?.id);
      setFilters({ company: firstCo?.id || "", payGroup: firstPg?.id || "", fy: data.currentFY, empType: "all" });
    });
  }, []);

  const loadDash = useCallback(() => {
    if (!filters.fy) return;
    const p = new URLSearchParams();
    if (filters.company) p.set("company", filters.company);
    if (filters.payGroup) p.set("payGroup", filters.payGroup);
    if (filters.fy) p.set("fy", filters.fy);
    if (filters.empType) p.set("empType", filters.empType);
    api.get(`/payroll/dashboard?${p.toString()}`).then(({ data }) => setDash(data));
  }, [filters]);

  useEffect(() => { loadDash(); }, [loadDash]);
  useEffect(() => { api.get("/payroll/runs").then(({ data }) => setRuns(data)); }, [dash]);
  useEffect(() => { if (tab === "structures" && !structs) api.get("/payroll/structures").then(({ data }) => setStructs(data)); }, [tab, structs]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 6000); };
  const act = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); flash(okMsg); loadDash(); return r; }
    catch (e) { alert(apiError(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(false); }
  };

  const start = (row) => {
    if (!filters.company || !filters.payGroup || filters.payGroup === "all")
      return alert("Select a specific Company and Pay Group to start a run.");
    return act(() => api.post("/payroll/runs/input-sync", {
      companyId: filters.company, payGroupId: filters.payGroup, month: row.month, year: row.year, type: "PAYROLL",
    }), `Input synced for ${row.label}`);
  };
  const process = (row) => act(() => api.post(`/payroll/runs/${row.runId}/process`), `Payroll processed for ${row.label}`);
  const approve = (row) => act(() => api.post(`/payroll/runs/${row.runId}/approve`), `${row.label} sent for approval & approved`);
  const release = (row) => act(() => api.post(`/payroll/runs/${row.runId}/release`), `Payslips released for ${row.label}`);
  const cancel = (row) => window.confirm(`Cancel the ${row.label} run? Payslips will be deleted.`) && act(() => api.post(`/payroll/runs/${row.runId}/cancel`), `${row.label} run cancelled`);
  const toggleLock = (row) => act(() => api.post(`/payroll/runs/${row.runId}/lock`, { locked: !row.locked }), row.locked ? "Pay group reopened" : "Pay group closed");

  const openSlips = async (row) => {
    setSlips({ run: row, items: null });
    const { data } = await api.get(`/payroll/runs/${row.runId}/payslips`);
    setSlips({ run: row, items: data });
  };
  const openDisc = async (row) => {
    const { data } = await api.get(`/payroll/runs/${row.runId}/discrepancies`);
    setDiscRow({ row, items: data });
  };

  const downloadSlip = (id) => downloadBlob(`/me/payslips/${id}/pdf`, `payslip-${id}.pdf`, setBusy);
  const holdSlip = async (p, held) => {
    await api.post(`/payroll/payslips/${p.id}/hold`, { held });
    openSlips(slips.run);
  };
  const saveStruct = async () => {
    setBusy(true);
    try {
      await api.put(`/payroll/structures/${editStruct.id}`, {
        basic: +editStruct.basic, hra: +editStruct.hra, conveyance: +editStruct.conveyance,
        special: +editStruct.special, other: +editStruct.other,
      });
      setEditStruct(null);
      const { data } = await api.get("/payroll/structures"); setStructs(data);
      flash("Salary structure updated");
    } catch (e) { alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!meta || !dash) return <Loading />;
  const activeCompanies = meta.companies.filter((c) => c.active);
  const pgOptions = meta.payGroups.filter((p) => !filters.company || filters.company === "all" || p.companyId === filters.company);

  return (
    <div>
      <PageHeader title="Payroll" subtitle="Multi-company · multi pay-group · 7-stage processing pipeline" />

      {msg && <div data-testid="payroll-flash" className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">{msg}</div>}

      <div className="mb-6 flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {[["processing", "Processing"], ["structures", "Salary Structures"], ["runs", "All Runs"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-accent text-accent" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>{l}</button>
        ))}
      </div>

      {tab === "processing" && (
        <>
          {/* Filters */}
          <div className="mb-6 flex flex-wrap items-end gap-3">
            <div>
              <label className="lbl mb-1 block">Group Company</label>
              <select data-testid="filter-company" value={filters.company} onChange={(e) => setFilters((f) => ({ ...f, company: e.target.value, payGroup: "all" }))} className="input w-auto min-w-[200px]">
                <option value="all">All Companies</option>
                {activeCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="lbl mb-1 block">Pay Group</label>
              <select data-testid="filter-paygroup" value={filters.payGroup} onChange={(e) => setFilters((f) => ({ ...f, payGroup: e.target.value }))} className="input w-auto min-w-[200px]">
                <option value="all">All Pay Groups</option>
                {pgOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="lbl mb-1 block">Financial Year</label>
              <select data-testid="filter-fy" value={filters.fy} onChange={(e) => setFilters((f) => ({ ...f, fy: e.target.value }))} className="input w-auto">
                {meta.financialYears.map((y) => <option key={y} value={y}>FY {y}</option>)}
              </select>
            </div>
            <div>
              <label className="lbl mb-1 block">Employee Type</label>
              <select data-testid="filter-emptype" value={filters.empType} onChange={(e) => setFilters((f) => ({ ...f, empType: e.target.value }))} className="input w-auto">
                <option value="all">All</option>
                {meta.employeeTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Summary cards */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[["Headcount", dash.summary.headcount, "bg-accent/10 text-accent"],
              ["Months Released", `${dash.summary.monthsReleased}/12`, "bg-emerald-500/10 text-emerald-600"],
              ["Months Pending", dash.summary.monthsPending, "bg-amber-500/10 text-amber-600"],
              ["Annual Net Disbursed", fmtMoney(dash.summary.annualNet), "bg-violet-500/10 text-violet-600"]].map(([l, v, a]) => (
              <Card key={l} className="p-5" data-testid={`pay-summary-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${a}`}><Banknote size={18} /></div>
                <p className="mt-3 font-head text-2xl font-extrabold text-slate-900 dark:text-white">{v}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">{l}</p>
              </Card>
            ))}
          </div>

          {/* Chart */}
          <Card className="mb-6 p-6">
            <h3 className="mb-4 font-head text-lg font-bold text-slate-900 dark:text-white">Net Pay vs Deductions · FY {dash.fy}</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dash.chart} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v} />
                  <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="net" name="Net Pay" fill="#16A34A" radius={[4, 4, 0, 0]} maxBarSize={26} />
                  <Bar dataKey="deductions" name="Deductions" fill="#DC2626" radius={[4, 4, 0, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Dashboard table */}
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="payroll-dashboard-table">
                <thead><tr>
                  <th className="th">Period</th><th className="th">Status</th><th className="th">Pipeline</th>
                  <th className="th">Employees</th><th className="th">Gross</th><th className="th">Net</th>
                  <th className="th">Acted By</th><th className="th text-right">Actions</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {dash.rows.map((row) => {
                    const [label, tone, stage] = STATUS[row.status] || ["—", "neutral", 0];
                    return (
                      <tr key={row.period} data-testid={`payroll-row-${row.period}`}>
                        <td className="td font-semibold text-slate-800 dark:text-slate-100">{row.label}</td>
                        <td className="td"><Badge tone={tone}>{label}</Badge></td>
                        <td className="td"><Stepper stage={stage} /></td>
                        <td className="td font-mono">{row.employeeCount || "—"}</td>
                        <td className="td font-mono">{row.totals?.gross ? fmtMoney(row.totals.gross) : "—"}</td>
                        <td className="td font-mono font-semibold text-emerald-600">{row.totals?.net ? fmtMoney(row.totals.net) : "—"}</td>
                        <td className="td text-xs text-slate-400">{row.actedBy || "—"}</td>
                        <td className="td">
                          <div className="flex flex-wrap items-center justify-end gap-1.5">
                            {row.status === "YET_TO_START" && (
                              <button disabled={busy} data-testid={`act-start-${row.period}`} onClick={() => start(row)} className="rounded bg-accent px-2.5 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"><Play size={11} className="mr-0.5 inline" /> Start</button>
                            )}
                            {row.status === "INPUT_SYNCED" && (
                              <>
                                <button disabled={busy} data-testid={`act-resync-${row.period}`} onClick={() => start(row)} className="rounded bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"><RefreshCw size={11} className="mr-0.5 inline" /> Resync</button>
                                <button disabled={busy} data-testid={`act-process-${row.period}`} onClick={() => process(row)} className="rounded bg-accent px-2.5 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">Process</button>
                              </>
                            )}
                            {(row.status === "PROCESSED" || row.status === "DISCREPANCY") && (
                              <>
                                <button data-testid={`act-disc-${row.period}`} onClick={() => openDisc(row)} className="rounded bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-500/15"><AlertTriangle size={11} className="mr-0.5 inline" /> Discrepancy</button>
                                <button data-testid={`act-slips-${row.period}`} onClick={() => openSlips(row)} className="rounded bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300">Report</button>
                                <button disabled={busy} data-testid={`act-approve-${row.period}`} onClick={() => approve(row)} className="rounded bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><Send size={11} className="mr-0.5 inline" /> Approve</button>
                                <button disabled={busy} data-testid={`act-cancel-${row.period}`} onClick={() => cancel(row)} className="rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-700 dark:bg-red-500/15"><Ban size={11} /></button>
                              </>
                            )}
                            {row.status === "APPROVED" && (
                              <>
                                <button data-testid={`act-slips-${row.period}`} onClick={() => openSlips(row)} className="rounded bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300">Report</button>
                                <button disabled={busy} data-testid={`act-release-${row.period}`} onClick={() => release(row)} className="rounded bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><CheckCircle2 size={11} className="mr-0.5 inline" /> Release</button>
                              </>
                            )}
                            {row.status === "RELEASED" && (
                              <>
                                <button data-testid={`act-slips-${row.period}`} onClick={() => openSlips(row)} className="rounded bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/15"><FileText size={11} className="mr-0.5 inline" /> Payslips</button>
                                <button data-testid={`act-lock-${row.period}`} onClick={() => toggleLock(row)} className="rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">{row.locked ? <Unlock size={11} /> : <Lock size={11} />}</button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {tab === "structures" && (!structs ? <Loading /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="structures-table">
              <thead><tr><th className="th">Employee</th><th className="th">Basic</th><th className="th">HRA</th><th className="th">Conveyance</th><th className="th">Special</th><th className="th">Other</th><th className="th">Gross / mo</th><th className="th">CTC / yr</th><th className="th"></th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {structs.map((s) => (
                  <tr key={s.id} data-testid={`struct-${s.id}`}>
                    <td className="td font-medium text-slate-800 dark:text-slate-100">{s.employeeName} <span className="font-mono text-xs text-slate-400">{s.employeeCode}</span></td>
                    <td className="td font-mono">{fmtMoney(s.basic)}</td><td className="td font-mono">{fmtMoney(s.hra)}</td>
                    <td className="td font-mono">{fmtMoney(s.conveyance || 0)}</td><td className="td font-mono">{fmtMoney(s.special || 0)}</td>
                    <td className="td font-mono">{fmtMoney(s.other || 0)}</td>
                    <td className="td font-mono font-semibold">{fmtMoney(s.grossMonthly)}</td><td className="td font-mono">{fmtMoney(s.ctc)}</td>
                    <td className="td"><button data-testid={`edit-struct-${s.id}`} onClick={() => setEditStruct({ id: s.id, name: s.employeeName, basic: s.basic, hra: s.hra, conveyance: s.conveyance || 0, special: s.special || 0, other: s.other || 0 })} className="text-accent hover:underline"><Pencil size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {tab === "runs" && (runs.length === 0 ? <EmptyState icon={FileText} title="No payroll runs yet" /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="runs-table">
              <thead><tr><th className="th">Period</th><th className="th">Type</th><th className="th">Company</th><th className="th">Pay Group</th><th className="th">Status</th><th className="th">Payslips</th><th className="th">Net</th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {runs.map((r) => {
                  const [label, tone] = STATUS[r.status] || [r.status, "neutral"];
                  return (
                    <tr key={r.id} data-testid={`run-${r.id}`}>
                      <td className="td font-semibold">{r.period}</td><td className="td">{r.type}</td>
                      <td className="td text-sm">{r.companyName || "—"}</td><td className="td text-sm">{r.payGroupName || "—"}</td>
                      <td className="td"><Badge tone={tone}>{label}</Badge></td>
                      <td className="td font-mono">{r.payslipCount}</td>
                      <td className="td font-mono font-semibold text-emerald-600">{r.totals?.net ? fmtMoney(r.totals.net) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {/* Payslips modal */}
      <Modal open={!!slips} onClose={() => setSlips(null)} title={slips ? `Payslips · ${slips.run.label}` : ""}
        footer={slips?.run?.status === "RELEASED" && (
          <div className="flex flex-wrap gap-2" data-testid="export-actions">
            <select data-testid="bankfile-select" onChange={(e) => { if (e.target.value) { downloadBlob(`/payroll/runs/${slips.run.runId}/bankfile?bank=${e.target.value}`, `bankfile-${e.target.value}-${slips.run.period}.csv`, setBusy); e.target.value = ""; } }} className="input w-auto py-1.5 text-xs"><option value="">Bank File…</option><option value="SBI">SBI</option><option value="ICICI">ICICI</option><option value="HDFC">HDFC</option></select>
            <button data-testid="dl-challan-pf" onClick={() => downloadBlob(`/payroll/runs/${slips.run.runId}/challan/pf`, `pf-${slips.run.period}.csv`, setBusy)} className="btn-outline px-3 py-1.5 text-xs">PF Challan</button>
            <button data-testid="dl-challan-tds" onClick={() => downloadBlob(`/payroll/runs/${slips.run.runId}/challan/tds`, `tds-${slips.run.period}.csv`, setBusy)} className="btn-outline px-3 py-1.5 text-xs">TDS Challan</button>
            <button data-testid="dl-report-salary" onClick={() => downloadBlob(`/payroll/runs/${slips.run.runId}/report/salary_details`, `salary-${slips.run.period}.csv`, setBusy)} className="btn-outline px-3 py-1.5 text-xs">Salary Report</button>
          </div>
        )}>
        {!slips?.items ? <Loading /> : (
          <div className="max-h-[60vh] overflow-y-auto overflow-x-auto">
            <table className="w-full text-sm" data-testid="payslip-table">
              <thead><tr><th className="th">Employee</th><th className="th">Gross</th><th className="th">Deductions</th><th className="th">Net</th><th className="th"></th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {slips.items.map((p) => (
                  <tr key={p.id} className={p.held ? "opacity-50" : ""}>
                    <td className="td">{p.employeeName} {p.held && <Badge tone="bad">Held</Badge>}</td>
                    <td className="td font-mono">{fmtMoney(p.gross)}</td>
                    <td className="td font-mono text-red-500">-{fmtMoney(p.deductions)}</td>
                    <td className="td font-mono font-semibold text-emerald-600">{fmtMoney(p.net)}</td>
                    <td className="td">
                      <div className="flex items-center gap-2">
                        <button onClick={() => downloadSlip(p.id)} data-testid={`dl-slip-${p.id}`} className="flex items-center gap-1 text-xs font-semibold text-accent hover:underline"><Download size={13} /> PDF</button>
                        <button onClick={() => holdSlip(p, !p.held)} data-testid={`hold-slip-${p.id}`} className="text-xs font-semibold text-slate-400 hover:text-amber-600">{p.held ? "Release" : "Hold"}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Discrepancy modal */}
      <Modal open={!!discRow} onClose={() => setDiscRow(null)} title={discRow ? `Discrepancy Sheet · ${discRow.row.label}` : ""}>
        {discRow && (discRow.items.length === 0
          ? <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:bg-emerald-500/10" data-testid="no-discrepancy"><CheckCircle2 size={16} /> No discrepancies found — all employees ready for payroll.</div>
          : (
            <div className="space-y-2" data-testid="discrepancy-list">
              {discRow.items.map((d, i) => (
                <div key={i} className="flex items-start gap-3 rounded-md border border-slate-100 px-3 py-2.5 dark:border-slate-800">
                  <Badge tone={d.severity === "HIGH" ? "bad" : d.severity === "MEDIUM" ? "warn" : "info"}>{d.severity}</Badge>
                  <div><p className="text-sm font-medium text-slate-800 dark:text-slate-100">{d.employeeName} <span className="font-mono text-xs text-slate-400">{d.code}</span></p><p className="text-xs text-slate-500 dark:text-slate-400">{d.message}</p></div>
                </div>
              ))}
            </div>
          ))}
      </Modal>

      {/* Edit structure modal */}
      <Modal open={!!editStruct} onClose={() => setEditStruct(null)} title={editStruct ? `Edit Salary · ${editStruct.name}` : ""}
        footer={<><button onClick={() => setEditStruct(null)} className="btn-outline">Cancel</button><button disabled={busy} onClick={saveStruct} data-testid="save-struct" className="btn-accent">{busy ? <Spinner className="text-white" /> : "Save"}</button></>}>
        {editStruct && (
          <div className="grid grid-cols-2 gap-4">
            {[["basic", "Basic"], ["hra", "HRA"], ["conveyance", "Conveyance"], ["special", "Special"], ["other", "Other"]].map(([k, l]) => (
              <div key={k}><label className="label">{l} (₹/mo)</label><input type="number" data-testid={`struct-${k}`} value={editStruct[k]} onChange={(e) => setEditStruct((s) => ({ ...s, [k]: e.target.value }))} className="input" /></div>
            ))}
            <div className="col-span-2 rounded-md bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800/50">Gross / month: <span className="font-mono font-semibold">{fmtMoney((+editStruct.basic) + (+editStruct.hra) + (+editStruct.conveyance) + (+editStruct.special) + (+editStruct.other))}</span></div>
          </div>
        )}
      </Modal>
    </div>
  );
}
